import { NavLink, useNavigate } from "react-router";
import { Library, FolderOpen, Sun, Moon, User, Brain, BookOpen, Settings, MoreHorizontal } from "lucide-react";
import { useTheme } from "./ThemeProvider";
import { useState } from "react";
import { SearchOverlay } from "./SearchOverlay";
import { CardDetailSheet } from "./CardDetailSheet";
import { MobileMoreSheet } from "./MobileMoreSheet";
import type { Thought } from "./ThoughtCard";

// PRD §4.2 — Nav order (locked): Library, Thinking Space, Collections, Editions.
// Map moves into Library as a view (Phase 5 work; route /map still exists during transition).
// Profile icon takes the former Settings slot. Settings lives inside Profile.
const NAV_ITEMS = [
  { to: "/library", label: "Library", icon: Library },
  { to: "/thinking", label: "Thinking Space", icon: Brain },
  { to: "/collections", label: "Collections", icon: FolderOpen },
  { to: "/editions", label: "Editions", icon: BookOpen },
];

export function AppNav() {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [selectedThought, setSelectedThought] = useState<Thought | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);

  return (
    <>
      <header
        className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/40"
        style={{ paddingTop: "var(--safe-top)" }}
      >
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Logo */}
            <button
              onClick={() => {
                if (window.location.pathname === "/") {
                  window.location.reload();
                } else {
                  navigate("/");
                }
              }}
              className="shrink-0 mr-6"
            >
              <span
                className="text-primary"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: "1.3rem",
                  fontWeight: 500,
                  fontStyle: "italic",
                }}
              >
                Burrow
              </span>
            </button>

            {/* Nav links */}
            <nav className="hidden md:flex items-center gap-1">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3.5 py-2 rounded-xl transition-all duration-200 ${
                      isActive
                        ? "bg-accent/15 text-accent"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/30"
                    }`
                  }
                  style={{ fontSize: "0.88rem" }}
                  end={item.to === "/"}
                >
                  <item.icon size={16} />
                  {item.label}
                </NavLink>
              ))}
            </nav>

            {/* Right controls — desktop */}
            <div className="hidden md:flex items-center gap-2.5 ml-auto">
              {/* `flex-1 min-w-0` lets the nav squeeze this below its content;
                  give it a floor once there's room, so the bar stops collapsing
                  to the point the placeholder truncates. */}
              <div className="min-w-0 lg:min-w-[16rem] flex-1 max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl">
                <SearchOverlay onSelectThought={(t) => setSelectedThought(t)} />
              </div>

              <button
                onClick={toggleTheme}
                className="p-2.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all"
              >
                {theme === "light" ? <Moon size={17} /> : <Sun size={17} />}
              </button>

              <button
                onClick={() => navigate("/u/me")}
                className="p-2.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all"
                title="Profile"
              >
                <User size={17} />
              </button>

              <button
                onClick={() => navigate("/settings")}
                className="p-2.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all"
                title="Settings"
              >
                <Settings size={17} />
              </button>
            </div>

            {/* Right controls — mobile: everything else lives in the bottom
                tab bar or the More sheet */}
            <div className="flex md:hidden items-center gap-1 ml-auto">
              <button
                onClick={toggleTheme}
                className="flex items-center justify-center w-11 h-11 rounded-xl text-muted-foreground hover:text-foreground transition-all"
                aria-label="Toggle theme"
              >
                {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
              </button>
              <button
                onClick={() => navigate("/u/me")}
                className="flex items-center justify-center w-11 h-11 rounded-xl text-muted-foreground hover:text-foreground transition-all"
                aria-label="Profile"
              >
                <User size={18} />
              </button>
              <button
                onClick={() => setMoreOpen(true)}
                className="flex items-center justify-center w-11 h-11 rounded-xl text-muted-foreground hover:text-foreground transition-all"
                aria-label="More"
              >
                <MoreHorizontal size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Search result detail sheet */}
      <CardDetailSheet
        thought={selectedThought}
        onClose={() => setSelectedThought(null)}
      />

      <MobileMoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} />
    </>
  );
}
